import React, { useEffect, useState } from "react";
import TutorDashboardLayout from "../../../layout/TutorDashboardLayout";
import { Switch, Modal, message, Button, TimePicker } from "antd";
import { useCreateAvailability } from "../../../hooks/api/Tutors/Availability/useCreateAvailability";
import { useGetTutorQuery } from "../../../hooks/api/Tutors/Auth/useGetTutorQuery";
import dayjs from "dayjs";
import Loader from "../../../components/Loader";

interface TimeSlot {
  startTime: string;
  endTime: string;
}

interface DayAvailability {
  day: string;
  timeSlots: TimeSlot[];
}

interface Payload {
  weeklyAvailability: DayAvailability[];
}

const initialState = [
  { day: "Sunday", timeSlots: [] },
  { day: "Monday", timeSlots: [] },
  { day: "Tuesday", timeSlots: [] },
  { day: "Wednesday", timeSlots: [] },
  { day: "Thursday", timeSlots: [] },
  { day: "Friday", timeSlots: [] },
  { day: "Saturday", timeSlots: [] },
];

const TutorAvailability = () => {
  const { data: tutor } = useGetTutorQuery();
  const [payload, setPayload] = useState<Payload>({
    weeklyAvailability: initialState,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tutor?.data?.weeklyAvailability?.length) {
      setPayload({ weeklyAvailability: tutor.data.weeklyAvailability });
    }
  }, [tutor]);

  const { mutateAsync, isLoading } = useCreateAvailability();

  const handleSubmit = async () => {
    setLoading(true);
    await mutateAsync(payload);
    message.success("Availability saved successfully");
    setLoading(false);
  };

  const handleToggleAvailability = (day: string, checked: boolean) => {
    if (!checked) {
      Modal.confirm({
        title: "Are you sure?",
        content: "Turning this off will remove all time slots for this day.",
        onOk: () => updateAvailability(day, checked),
      });
    } else {
      updateAvailability(day, checked);
    }
  };

  const updateAvailability = (day: string, checked: boolean) => {
    setPayload((prev) => ({
      ...prev,
      weeklyAvailability: prev.weeklyAvailability.map((item) =>
        item.day === day
          ? {
              ...item,
              timeSlots: checked
                ? [{ startTime: "08:00", endTime: "09:00" }]
                : [],
            }
          : item
      ),
    }));
  };

  const handleTimeChange = (day: string, index: number, value: any) => {
    if (value) {
      setPayload((prev) => ({
        ...prev,
        weeklyAvailability: prev.weeklyAvailability.map((item) =>
          item.day === day
            ? {
                ...item,
                timeSlots: item.timeSlots.map((slot, i) =>
                  i === index
                    ? {
                        ...slot,
                        startTime: value.format("HH:00"),
                        endTime: dayjs(value).add(1, "hour").format("HH:00"),
                      }
                    : slot
                ),
              }
            : item
        ),
      }));
    }
  };

  const addTimeSlot = (day: string) => {
    setPayload((prev) => ({
      ...prev,
      weeklyAvailability: prev.weeklyAvailability.map((item) =>
        item.day === day
          ? {
              ...item,
              timeSlots: [
                ...item.timeSlots,
                { startTime: "08:00", endTime: "09:00" },
              ],
            }
          : item
      ),
    }));
  };

  const removeTimeSlot = (day: string, index: number) => {
    setPayload((prev) => ({
      ...prev,
      weeklyAvailability: prev.weeklyAvailability.map((item) =>
        item.day === day
          ? { ...item, timeSlots: item.timeSlots.filter((_, i) => i !== index) }
          : item
      ),
    }));
  };

  return (
    <TutorDashboardLayout>
      <section className="p-6 bg-white rounded-lg shadow-md my-4">
        <h3 className="text-[20px] W2STBold">Set Availability</h3>
        <p className="text-gray-500 mt-2">
          Customize your weekly availability for tutoring sessions.
        </p>

        <div className="flex justify-end mt-6">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-black shadow-md text-white px-6 py-3 rounded-md font-medium hover:bg-purple-700 transition disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>

        {isLoading && <Loader />}

        {/* {!isLoading && payload && ( */}
        <div className="mt-4 space-y-4">
          {!isLoading &&
            payload &&
            payload.weeklyAvailability
              // .filter((day) => day.timeSlots.length > 0)
              .map((availability) => (
                <div className="p-4 border rounded-md" key={availability.day}>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-4 font-medium">
                      <Switch
                        checked={availability.timeSlots.length > 0}
                        onChange={(checked) =>
                          handleToggleAvailability(availability.day, checked)
                        }
                      />
                      <span>{availability.day}</span>
                    </div>
                    <Button
                      onClick={() => addTimeSlot(availability.day)}
                      type="primary"
                      size="small"
                    >
                      Add Time Slot
                    </Button>
                  </div>
                  {availability.timeSlots.map((slot, index) => (
                    <div key={index} className="flex items-center gap-4 mt-2">
                      <div className="w-6/12">
                        <div>Start Time:</div>
                        <TimePicker
                          format="HH:00"
                          minuteStep={30}
                          value={dayjs(slot.startTime, "HH:mm")}
                          onChange={(value) =>
                            handleTimeChange(availability.day, index, value)
                          }
                        />
                      </div>
                      <div className="w-6/12">
                        <div>End Time:</div>
                        <TimePicker
                          format="HH:00"
                          value={dayjs(slot.endTime, "HH:mm")}
                          disabled
                        />
                      </div>
                      <Button
                        danger
                        size="small"
                        onClick={() => removeTimeSlot(availability.day, index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ))}
        </div>
      </section>
    </TutorDashboardLayout>
  );
};

export default TutorAvailability;
